import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, combineLatest, distinctUntilChanged, map } from 'rxjs';

import { ApprovalsInboxService } from 'src/app/services/approvals-inbox/approvals-inbox.service';
import { TasksService } from 'src/app/services/tasks/tasks.service';
import { NeedsYouItem, needsYouItems } from 'src/app/utils/needs-you';

/**
 * The one "needs you" count (Vincent, 2026-11-03: "Yes, count them"): every
 * card waiting on the person — a question in an agent's own chat with no task
 * behind it included — and every open task waiting on them, each counted once
 * (utils/needs-you.ts). The rail's Dashboard row badges it and the dashboard
 * lists it. Before, the Dashboard badge counted tool approvals and plans only
 * and the Tasks badge the tasks: a question card with no task counted nowhere,
 * and a card behind a task could count on both rows.
 *
 * Both sources are scoped to the organization on screen and re-bind
 * themselves (org switch, region re-bind): this only merges them. Null until
 * either has answered.
 */
@Injectable({ providedIn: 'root' })
export class NeedsYouService implements OnDestroy {
  public readonly items$ = new BehaviorSubject<NeedsYouItem[] | null>(null);
  public readonly count$: Observable<number> = this.items$.pipe(map((items) => items?.length ?? 0), distinctUntilChanged());

  private sub: Subscription | null = null;

  constructor(
    private approvals: ApprovalsInboxService,
    private tasks: TasksService,
  ) {}

  public get count(): number {
    return this.items$.value?.length ?? 0;
  }

  /** Idempotent; starts both sources (themselves idempotent) and merges them. */
  public start(): void {
    this.approvals.start();
    this.tasks.start();
    // Partial doubles (specs of the surfaces that start this) may not expose the streams: the count is optional chrome.
    if (this.sub || !this.approvals.pending$ || !this.tasks.tasks$) return;
    this.sub = combineLatest([this.approvals.pending$, this.tasks.tasks$]).subscribe(([cards, tasks]) => {
      if (cards === null && tasks === null) {
        this.items$.next(null);
        return;
      }
      this.items$.next(needsYouItems(cards ?? [], (tasks ?? []).filter(TasksService.needsYou)));
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.sub = null;
  }
}
